/**
 * R235 (2026-10-20, user request): parser input koordinat lokasi absensi —
 * menerima Plus Code Google Maps (kode penuh), tautan Google Maps, atau
 * "lat,lng" polos, lalu menghasilkan latitude/longitude. Offline penuh —
 * tanpa API eksternal. Tautan pendek maps.app.goo.gl tidak dapat di-resolve
 * offline → error berisi panduan.
 */

const CODE_ALPHABET = "23456789CFGHJMPQRVWX";
const PAIR_RESOLUTIONS = [20.0, 1.0, 0.05, 0.0025, 0.000125];

/** Decode Plus Code PENUH (mis. "6P57FR92+JVR6") → pusat cell (±14 m).
 *  Kode pendek (mis. "FR92+JV") mengembalikan null — butuh referensi kota. */
export function decodePlusCode(code) {
  const clean = String(code || "").trim().toUpperCase().replace(/\s+/g, "");
  const m = clean.match(/^([23456789CFGHJMPQRVWX]{8})\+([23456789CFGHJMPQRVWX]{2,})$/);
  if (!m) return null;
  const chars = (m[1] + m[2]).slice(0, 10); // abaikan grid refinement >10 char
  const pairCount = Math.floor(chars.length / 2);
  if (pairCount < 5) return null;
  let lat = 0;
  let lng = 0;
  for (let i = 0; i < pairCount; i++) {
    lat += CODE_ALPHABET.indexOf(chars[i * 2]) * PAIR_RESOLUTIONS[i];
    lng += CODE_ALPHABET.indexOf(chars[i * 2 + 1]) * PAIR_RESOLUTIONS[i];
  }
  const res = PAIR_RESOLUTIONS[pairCount - 1];
  return {
    latitude: Math.round((lat - 90 + res / 2) * 1e6) / 1e6,
    longitude: Math.round((lng - 180 + res / 2) * 1e6) / 1e6,
  };
}

/** Parse input koordinat bebas → { ok, latitude, longitude, source } atau
 *  { ok:false, error } (error = panduan perbaikan berbahasa Indonesia). */
export function parseKoordinatInput(raw) {
  const s = String(raw || "").trim();
  if (!s) return { ok: false, error: null };

  // 1. Plus Code (penuh; pendek → panduan).
  if (/^[23456789CFGHJMPQRVWX]{2,8}\+/i.test(s) && !s.includes("/") && !s.includes(".")) {
    const full = decodePlusCode(s);
    if (full) return { ok: true, ...full, source: "plus_code" };
    if (!/^[23456789CFGHJMPQRVWX]{8}\+/i.test(s)) {
      return { ok: false, error: "Plus Code pendek memerlukan nama kota — salin kode PENUH (mis. 6P58RRCG+WV) dari Google Maps, atau gunakan tautan Google Maps." };
    }
    return { ok: false, error: "Plus Code tidak valid." };
  }

  // 2. Tautan Google Maps.
  if (/google\.|maps\.app\.goo\.gl|goo\.gl/i.test(s)) {
    if (/maps\.app\.goo\.gl|goo\.gl\/maps/i.test(s)) {
      return { ok: false, error: "Tautan pendek tidak dapat dibaca otomatis — buka tautan di browser, lalu salin URL LENGKAP dari address bar (yang mengandung @lat,lng)." };
    }
    const at = s.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
    if (at) return { ok: true, latitude: Number(at[1]), longitude: Number(at[2]), source: "gmaps_url" };
    const q = s.match(/[?&](?:q|query|ll)=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
    if (q) return { ok: true, latitude: Number(q[1]), longitude: Number(q[2]), source: "gmaps_url" };
    const d34 = s.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
    if (d34) return { ok: true, latitude: Number(d34[1]), longitude: Number(d34[2]), source: "gmaps_url" };
    return { ok: false, error: "Koordinat tidak ditemukan pada tautan — pastikan URL Google Maps mengandung @latitude,longitude." };
  }

  // 3. "lat, lng" polos.
  const ll = s.match(/^(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)$/);
  if (ll) {
    const lat = Number(ll[1]);
    const lng = Number(ll[2]);
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      return { ok: false, error: "Latitude harus antara -90 s.d. 90 dan longitude -180 s.d. 180." };
    }
    return { ok: true, latitude: lat, longitude: lng, source: "manual" };
  }

  return { ok: false, error: "Format tidak dikenali. Isi Plus Code penuh, tautan Google Maps, atau koordinat \"lat,lng\"." };
}

/** Zona waktu IANA untuk dropdown searchable — Indonesia disematkan di atas. */
export function timezoneOptions() {
  const pinned = ["Asia/Jakarta", "Asia/Makassar", "Asia/Jayapura"];
  let all = [];
  try {
    all = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  } catch { all = []; }
  if (all.length === 0) all = [...pinned, "Asia/Singapore", "Asia/Kuala_Lumpur", "UTC"];
  const rest = all.filter((z) => !pinned.includes(z));
  return [...pinned, ...rest].map((z) => ({ value: z, label: z.replace(/_/g, " ") }));
}
